/**
 * VariableDefinitionEditor - Unified component for managing variable/field definitions.
 *
 * This is the single source of truth for editing variable definitions across the app.
 * It replaces the previously duplicated implementations in:
 * - FieldDefinitionEditor (now a wrapper for backwards compatibility)
 * - TemplateVariableEditor (now a wrapper for backwards compatibility)
 * - AgentModal inline code (~320 lines removed)
 *
 * Features are configurable via props to support different contexts:
 * - Template variables (email, messaging)
 * - Automation flow inputs
 * - AI agent input variables
 * - Target list column definitions
 */

import { faKey, faPlus, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	VARIABLE_TYPE_REGISTRY,
	VariableFieldType,
	VariableTypeKey,
	type VariableDefinitionModel,
} from '@supply-mover-2-0/shared';
import { useMemo } from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { Button } from './Button';
import { Input } from './Input';
import type { SelectSearchGroup } from './SelectSearch';
import { SelectSearch } from './SelectSearch';

/**
 * Type option for the type selector dropdown.
 */
export interface TypeOption {
	value: string;
	label: string;
	subtitle?: string;
	baseType: VariableFieldType;
	complexTypeKey?: VariableTypeKey;
	isArray?: boolean;
}

/**
 * Build all available type options including primitives and complex types.
 */
export function buildTypeOptions(): Array<TypeOption> {
	const options: Array<TypeOption> = [];

	// Primitive types
	options.push({
		value: VariableFieldType.STRING,
		label: 'String',
		baseType: VariableFieldType.STRING,
		subtitle: 'Text value',
	});
	options.push({
		value: VariableFieldType.NUMBER,
		label: 'Number',
		baseType: VariableFieldType.NUMBER,
		subtitle: 'Numeric value',
	});
	options.push({
		value: VariableFieldType.BOOLEAN,
		label: 'Boolean',
		baseType: VariableFieldType.BOOLEAN,
		subtitle: 'True/False',
	});
	options.push({
		value: VariableFieldType.DATE,
		label: 'Date',
		baseType: VariableFieldType.DATE,
		subtitle: 'Date value',
	});
	options.push({
		value: VariableFieldType.EMAIL,
		label: 'Email',
		baseType: VariableFieldType.EMAIL,
		subtitle: 'Email address',
	});
	options.push({
		value: VariableFieldType.PHONE,
		label: 'Phone',
		baseType: VariableFieldType.PHONE,
		subtitle: 'Phone number',
	});
	options.push({
		value: VariableFieldType.CURRENCY,
		label: 'Currency',
		baseType: VariableFieldType.CURRENCY,
		subtitle: 'Money value',
	});

	// Complex types from registry (as objects)
	for (const key of Object.values(VariableTypeKey)) {
		const typeDef = VARIABLE_TYPE_REGISTRY[key];

		if (typeDef != null) {
			options.push({
				value: `object:${key}`,
				label: typeDef.label,
				subtitle: typeDef.description,
				baseType: VariableFieldType.OBJECT,
				complexTypeKey: key,
				isArray: false,
			});
		}
	}

	// Complex types as arrays
	for (const key of Object.values(VariableTypeKey)) {
		const typeDef = VARIABLE_TYPE_REGISTRY[key];

		if (typeDef != null) {
			options.push({
				value: `array:${key}`,
				label: `Array of ${typeDef.label}`,
				subtitle: `List of ${typeDef.label.toLowerCase()} items`,
				baseType: VariableFieldType.ARRAY,
				complexTypeKey: key,
				isArray: true,
			});
		}
	}

	return options;
}

export const TYPE_OPTIONS = buildTypeOptions();

/**
 * Get the combined value string from a variable's type configuration.
 */
export function getTypeValue(variable: VariableDefinitionModel): string {
	if (
		variable.type === VariableFieldType.OBJECT &&
		variable.objectType != null
	) {
		return `object:${variable.objectType}`;
	}

	if (variable.type === VariableFieldType.ARRAY && variable.arrayType != null) {
		return `array:${variable.arrayType}`;
	}

	return variable.type;
}

/**
 * Parse a combined value string into type configuration.
 */
export function parseTypeValue(value: string): {
	type: VariableFieldType;
	objectType?: VariableTypeKey;
	arrayType?: VariableTypeKey;
} {
	if (value.startsWith('object:')) {
		const key = value.substring(7) as VariableTypeKey;

		return { type: VariableFieldType.OBJECT, objectType: key };
	}

	if (value.startsWith('array:')) {
		const key = value.substring(6) as VariableTypeKey;

		return { type: VariableFieldType.ARRAY, arrayType: key };
	}

	return { type: value as VariableFieldType };
}

/**
 * Build grouped type options for SelectSearch.
 */
export function buildTypeSearchGroups(
	t: (key: string) => string,
): Array<SelectSearchGroup> {
	const groups: Array<SelectSearchGroup> = [];

	// Primitives group
	const primitives = TYPE_OPTIONS.filter((opt) => opt.complexTypeKey == null);

	if (primitives.length > 0) {
		groups.push({
			key: 'primitives',
			label: t('Primitives'),
			options: primitives.map((opt) => ({
				value: opt.value,
				label: t(opt.label),
				subtitle: opt.subtitle != null ? t(opt.subtitle) : undefined,
			})),
		});
	}

	// Group complex types by their type key (Order, Client, etc.)
	const typeKeys = Object.values(VariableTypeKey);

	for (const typeKey of typeKeys) {
		const typeDef = VARIABLE_TYPE_REGISTRY[typeKey];

		if (typeDef == null) {
			continue;
		}

		// Find single and array options for this type
		const singleOption = TYPE_OPTIONS.find(
			(opt) => opt.complexTypeKey === typeKey && !opt.isArray,
		);
		const arrayOption = TYPE_OPTIONS.find(
			(opt) => opt.complexTypeKey === typeKey && opt.isArray,
		);

		const options: Array<{
			value: string;
			label: string;
			subtitle?: string;
		}> = [];

		if (singleOption != null) {
			options.push({
				value: singleOption.value,
				label: t(singleOption.label),
				subtitle:
					singleOption.subtitle != null ? t(singleOption.subtitle) : undefined,
			});
		}

		if (arrayOption != null) {
			options.push({
				value: arrayOption.value,
				label: t(arrayOption.label),
				subtitle:
					arrayOption.subtitle != null ? t(arrayOption.subtitle) : undefined,
			});
		}

		if (options.length > 0) {
			groups.push({
				key: typeKey,
				label: t(typeDef.label),
				options,
			});
		}
	}

	return groups;
}

export interface VariableDefinitionEditorProps {
	/** Current list of variables */
	variables: Array<VariableDefinitionModel>;
	/** Callback when variables change */
	onChange: (variables: Array<VariableDefinitionModel>) => void;
	/** Whether the editor is disabled (read-only) */
	disabled?: boolean;
	/** Title for the section header */
	title?: string;
	/** Whether to show the card wrapper */
	showCard?: boolean;
	/** Empty state message */
	emptyMessage?: string;
	/** Whether to show the label field */
	showLabel?: boolean;
	/** Whether to show the description field */
	showDescription?: boolean;
	/** Whether to show the default value field */
	showDefaultValue?: boolean;
	/** Whether to show the required toggle */
	showRequired?: boolean;
	/** Whether to show the immutable toggle */
	showImmutable?: boolean;
	/** Whether to show primary key selection */
	showPrimaryKey?: boolean;
	/** Label for primary key field */
	primaryKeyLabel?: string;
	/** Minimum number of fields required */
	minFields?: number;
	/** Maximum number of fields allowed */
	maxFields?: number;
	/** Whether to allow an empty state with add button */
	allowEmpty?: boolean;
	/** Custom class name for the container */
	className?: string;
	/**
	 * AGENT OUTPUT variables only: the names of input variables an output may
	 * fall back to. When provided (and non-empty), each variable row gains a
	 * "Fall back to input" toggle + picker that sets `fallbackFromInput`.
	 * Omit it for input-variable / template / column editing.
	 */
	fallbackInputOptions?: Array<string>;
}

/**
 * VariableDefinitionEditor - A unified component for managing variable/field definitions.
 *
 * Used by:
 * - Email and messaging template editors
 * - Automation flow start step configuration
 * - AI agent input variables
 * - Target list column definitions
 *
 * Features:
 * - Add/remove variables
 * - Edit variable name, label, type, required flag, and description
 * - Support for complex types (objects, arrays) from the type registry
 * - Primary key selection for column definitions
 * - Consistent styling with the design system
 */
export function VariableDefinitionEditor({
	variables,
	onChange,
	disabled = false,
	title = 'Variables',
	showCard = false,
	emptyMessage = 'No variables defined',
	showLabel = false,
	showDescription = true,
	showDefaultValue = false,
	showRequired = true,
	showImmutable = false,
	showPrimaryKey = false,
	primaryKeyLabel = 'Primary Key',
	minFields = 0,
	maxFields = undefined,
	allowEmpty = true,
	className,
	fallbackInputOptions,
}: VariableDefinitionEditorProps) {
	const colors = useThemeColors();
	const { t } = useTranslation();

	const typeSearchGroups = useMemo(() => buildTypeSearchGroups(t), [t]);

	// Drop blank names — input variables mid-edit can have an empty name.
	const validFallbackOptions = useMemo(
		() => (fallbackInputOptions ?? []).filter((name) => name.trim() !== ''),
		[fallbackInputOptions],
	);

	const handleAddVariable = () => {
		const newVariable: VariableDefinitionModel = {
			name: '',
			type: VariableFieldType.STRING,
			required: false,
		};

		if (showPrimaryKey && variables.length === 0) {
			newVariable.isPrimaryKey = true;
		}

		onChange([...variables, newVariable]);
	};

	const handleUpdateVariable = (
		index: number,
		updates: Partial<VariableDefinitionModel>,
	) => {
		const updatedVariables = [...variables];

		// Handle primary key - only one can be selected
		if (showPrimaryKey && updates.isPrimaryKey === true) {
			updatedVariables.forEach((v, i) => {
				if (i !== index) {
					v.isPrimaryKey = false;
				}
			});
		}

		const existingVariable = updatedVariables[index];
		if (existingVariable == null) {
			return;
		}
		updatedVariables[index] = {
			...existingVariable,
			...updates,
		};
		onChange(updatedVariables);
	};

	const handleTypeChange = (index: number, typeValue: string) => {
		const parsed = parseTypeValue(typeValue);
		handleUpdateVariable(index, {
			type: parsed.type,
			objectType: parsed.objectType,
			arrayType: parsed.arrayType,
		});
	};

	const handleRemoveVariable = (index: number) => {
		const variableToRemove = variables[index];
		const updatedVariables = variables.filter((_, i) => i !== index);

		// If removing the primary key, set the first one as primary
		const firstRemaining = updatedVariables[0];
		if (
			showPrimaryKey &&
			variableToRemove?.isPrimaryKey === true &&
			firstRemaining != null
		) {
			firstRemaining.isPrimaryKey = true;
		}

		onChange(updatedVariables);
	};

	const canAddMore = maxFields === undefined || variables.length < maxFields;
	const canRemove = variables.length > minFields;

	// Empty state with add button
	if (!allowEmpty && variables.length === 0) {
		return (
			<div className={cn('text-center py-8', className)}>
				<p className={`mb-4 ${colors.classes.textSecondary}`}>
					{t(emptyMessage)}
				</p>
				{!disabled && (
					<Button
						size="sm"
						variant="secondary"
						onClick={handleAddVariable}
						icon={<FontAwesomeIcon icon={faPlus} />}
					>
						{t('Add Field')}
					</Button>
				)}
			</div>
		);
	}

	const content = (
		<div className={cn('space-y-4', className)}>
			{title != null && (
				<div className="flex justify-between items-center">
					<h2 className={`text-lg font-medium ${colors.classes.textPrimary}`}>
						{t(title)}
					</h2>
					{!disabled && canAddMore && (
						<Button
							size="sm"
							variant="outline"
							icon={<FontAwesomeIcon icon={faPlus} />}
							onClick={handleAddVariable}
						>
							{t('Add')}
						</Button>
					)}
				</div>
			)}

			{showPrimaryKey && variables.length > 0 && (
				<div className={`text-sm ${colors.classes.textSecondary} mb-2`}>
					<FontAwesomeIcon icon={faKey} className="mr-1" />
					{t(primaryKeyLabel)}: {t('Select one field as the unique identifier')}
				</div>
			)}

			{variables.length === 0 ? (
				<div
					className={`text-sm ${colors.classes.textSecondary} text-center py-4`}
				>
					{t(emptyMessage)}
				</div>
			) : (
				<div className="space-y-3">
					{variables.map((variable, index) => (
						<div
							key={index}
							className={cn(
								'p-3 border rounded-lg',
								colors.classes.borderPrimary,
								colors.classes.bgSecondary,
								showPrimaryKey && variable.isPrimaryKey
									? 'ring-2 ring-blue-500 ring-opacity-50'
									: '',
							)}
						>
							<div className="space-y-2">
								{/* Header row with primary key and delete */}
								<div className="flex justify-between items-start gap-2">
									<div className="flex items-center gap-2 flex-1">
										{showPrimaryKey && (
											<div>
												<input
													type="radio"
													name="primaryKey"
													checked={variable.isPrimaryKey ?? false}
													onChange={(e) => {
														handleUpdateVariable(index, {
															isPrimaryKey: e.target.checked,
														});
													}}
													disabled={disabled}
													title={t('Set as primary key')}
												/>
											</div>
										)}
										<Input
											value={variable.name}
											onChange={(e) => {
												handleUpdateVariable(index, { name: e.target.value });
											}}
											placeholder={t('Variable name')}
											size="sm"
											disabled={disabled}
											className="flex-1"
										/>
										{showPrimaryKey && variable.isPrimaryKey && (
											<div className="flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-900 rounded text-xs">
												<FontAwesomeIcon
													icon={faKey}
													className="mr-1 text-blue-600 dark:text-blue-400"
												/>
												<span className="text-blue-700 dark:text-blue-300">
													{t('Primary')}
												</span>
											</div>
										)}
									</div>
									{!disabled && canRemove && (
										<Button
											size="xs"
											variant="ghost"
											icon={<FontAwesomeIcon icon={faTrash} />}
											onClick={() => {
												handleRemoveVariable(index);
											}}
										/>
									)}
								</div>

								{/* Label field */}
								{showLabel && (
									<Input
										value={variable.label ?? ''}
										onChange={(e) => {
											handleUpdateVariable(index, { label: e.target.value });
										}}
										placeholder={t('Display Label (optional)')}
										size="sm"
										disabled={disabled}
									/>
								)}

								{/* Type selector */}
								<SelectSearch
									mode="static"
									value={getTypeValue(variable)}
									onChange={(value) => {
										handleTypeChange(index, value);
									}}
									placeholder={t('Select type...')}
									groups={typeSearchGroups}
									fullWidth
									disabled={disabled}
								/>

								{/* Toggles row */}
								<div className="flex items-center gap-4">
									{showRequired && (
										<label className="flex items-center gap-2 text-sm">
											<input
												type="checkbox"
												checked={variable.required ?? false}
												onChange={(e) => {
													handleUpdateVariable(index, {
														required: e.target.checked,
													});
												}}
												className="rounded"
												disabled={disabled}
											/>
											<span className={colors.classes.textSecondary}>
												{t('Required')}
											</span>
										</label>
									)}

									{showImmutable && (
										<label className="flex items-center gap-2 text-sm">
											<input
												type="checkbox"
												checked={variable.immutable !== false}
												onChange={(e) => {
													handleUpdateVariable(index, {
														immutable: e.target.checked,
													});
												}}
												className="rounded"
												disabled={disabled}
											/>
											<span className={colors.classes.textSecondary}>
												{t('Immutable')}
											</span>
										</label>
									)}
								</div>

								{/* Fall back to an input variable — agent output variables only */}
								{validFallbackOptions.length > 0 && (
									<div>
										<label className="flex items-center gap-2 text-sm">
											<input
												type="checkbox"
												checked={variable.fallbackFromInput != null}
												onChange={(e) => {
													if (e.target.checked) {
														const sameName = validFallbackOptions.find(
															(name) => name === variable.name,
														);
														handleUpdateVariable(index, {
															fallbackFromInput:
																sameName ?? validFallbackOptions[0],
														});
													} else {
														handleUpdateVariable(index, {
															fallbackFromInput: undefined,
														});
													}
												}}
												className="rounded"
												disabled={disabled}
											/>
											<span className={colors.classes.textSecondary}>
												{t('Fall back to an input variable')}
											</span>
										</label>
										{variable.fallbackFromInput != null && (
											<div className="ml-6 mt-2">
												<SelectSearch
													mode="static"
													value={variable.fallbackFromInput}
													onChange={(value) => {
														handleUpdateVariable(index, {
															fallbackFromInput: value,
														});
													}}
													placeholder={t('Select input variable...')}
													groups={[
														{
															key: 'inputs',
															label: t('Input variables'),
															options: validFallbackOptions.map((name) => ({
																value: name,
																label: name,
															})),
														},
													]}
													fullWidth
													disabled={disabled}
												/>
												<p
													className={cn(
														'text-xs mt-1',
														colors.classes.textSecondary,
													)}
												>
													{t(
														"When the call doesn't capture this value, the input variable's value is used instead.",
													)}
												</p>
											</div>
										)}
									</div>
								)}

								{/* Default value */}
								{showDefaultValue && (
									<Input
										value={
											variable.defaultValue != null
												? String(variable.defaultValue)
												: ''
										}
										onChange={(e) => {
											handleUpdateVariable(index, {
												defaultValue: e.target.value || undefined,
											});
										}}
										placeholder={t('Default value (optional)')}
										size="sm"
										disabled={disabled}
									/>
								)}

								{/* Description */}
								{showDescription && (
									<Input
										value={variable.description ?? ''}
										onChange={(e) => {
											handleUpdateVariable(index, {
												description: e.target.value,
											});
										}}
										placeholder={t('Description (optional)')}
										size="sm"
										disabled={disabled}
									/>
								)}

								{/* Variable syntax preview */}
								{variable.name != null && variable.name.trim() !== '' && (
									<div className={`text-xs ${colors.classes.textSecondary}`}>
										{t('Variable')}:{' '}
										<code className="px-1 py-0.5 rounded bg-black bg-opacity-10">
											{`{{${variable.name}}}`}
										</code>
									</div>
								)}
							</div>
						</div>
					))}
				</div>
			)}

			{/* Add button at bottom when no title header */}
			{title == null && !disabled && canAddMore && (
				<Button
					size="sm"
					variant="secondary"
					onClick={handleAddVariable}
					icon={<FontAwesomeIcon icon={faPlus} />}
					className="w-full"
				>
					{t('Add Field')}
				</Button>
			)}
		</div>
	);

	if (showCard) {
		return (
			<div
				className={cn('border rounded-lg p-6', colors.classes.borderPrimary)}
			>
				{content}
			</div>
		);
	}

	return content;
}
